import React, { Component, ErrorInfo, ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface Props {
    children: ReactNode;
    /** Shown above the error: which part of the screen stopped. */
    where: string;
}

interface State {
    error?: Error;
    stack?: string;
}

const CopyError = ({ text }: { text: string }) => {
    const [copied, setCopied] = useState(false);
    return (
        <button
            className="btn sm"
            onClick={async () => {
                await navigator.clipboard?.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            }}
        >
            {copied ? 'Gekopieerd' : 'Kopieer fout'}
        </button>
    );
};

/**
 * Catches a render error so one screen that trips over unexpected data shows what broke instead of
 * blanking the whole page; React unmounts everything above an uncaught error.
 */
export class ErrorBoundary extends Component<Props, State> {
    state: State = {};

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        this.setState({ stack: info.componentStack ?? undefined });
        // eslint-disable-next-line no-console
        console.error(`v2 ${this.props.where} crashed`, error, info.componentStack);
    }

    render() {
        const { error, stack } = this.state;
        if (!error) return this.props.children;

        const text = `${this.props.where}: ${error.message}\n${window.location.pathname}\n${error.stack ?? ''}\n${stack ?? ''}`;

        return (
            <div className="v2" style={{ padding: 24 }}>
                <div className="notice" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <b>Dit scherm liep vast ({this.props.where})</b>
                    <span className="mono">{error.message}</span>
                    <span className="hrow" style={{ gap: 8 }}>
                        <button className="btn sm" onClick={() => window.location.reload()}>
                            Opnieuw laden
                        </button>
                        <CopyError text={text} />
                    </span>
                </div>
            </div>
        );
    }
}

/** One boundary per screen, reset when you navigate, so the shell stays usable after a crash. */
export const ScreenBoundary = ({ children }: { children: ReactNode }) => {
    const { pathname } = useLocation();
    return (
        <ErrorBoundary key={pathname} where="scherm">
            {children}
        </ErrorBoundary>
    );
};
